import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import {
  SHIPPING_PREFERENCE_OWN,
  SHIPPING_PREFERENCE_PLATFORM,
  hasValidPlatformShippingDetails,
} from '../../util/shipping';
import { fetchCurrentUser, setCurrentUser } from '../../ducks/user.duck';

const PER_PAGE = 100;

const queryOwnListingsPage = (sdk, page) =>
  sdk.ownListings.query({
    perPage: PER_PAGE,
    page,
  });

const queryAllOwnListings = sdk => {
  const queryPage = (page, collected) =>
    queryOwnListingsPage(sdk, page).then(response => {
      const listings = denormalisedResponseEntities(response);
      const { totalPages = page } = response.data.meta || {};
      const nextCollected = collected.concat(listings);
      return page < totalPages ? queryPage(page + 1, nextCollected) : nextCollected;
    });
  return queryPage(1, []);
};

const buildShippingPreferences = values => {
  const shippingPreference = values.shippingPreference || SHIPPING_PREFERENCE_PLATFORM;
  const isOwn = shippingPreference === SHIPPING_PREFERENCE_OWN;
  const isPlatform = shippingPreference === SHIPPING_PREFERENCE_PLATFORM;

  return {
    shippingPreference,
    defaultShippingFeeInSubunits: isOwn ? values.defaultShippingFee?.amount : null,
    defaultPackage: isPlatform
      ? {
          length: Number(values.packageLength),
          width: Number(values.packageWidth),
          height: Number(values.packageHeight),
          distanceUnit: values.packageDimensionUnit,
          dimensionUnit: values.packageDimensionUnit,
          weight: Number(values.packageWeight),
          massUnit: values.packageWeightUnit,
          weightUnit: values.packageWeightUnit,
        }
      : null,
    defaultOrigin: isPlatform
      ? {
          name: values.originName,
          street1: values.originStreet1,
          street2: values.originStreet2 || null,
          city: values.originCity,
          state: values.originState,
          postalCode: values.originPostalCode,
          country: values.originCountry,
          phone: values.originPhone || null,
          email: values.originEmail || null,
        }
      : null,
  };
};

const shouldUpdateListing = (listing, previousPreference) => {
  const { state, publicData = {} } = listing?.attributes || {};
  const listingPreference = publicData.shippingPreference || SHIPPING_PREFERENCE_OWN;
  return (
    state === 'published' && publicData.shippingEnabled && listingPreference === previousPreference
  );
};

const buildListingUpdate = (listing, shippingPreferences) => {
  const { shippingPreference, defaultShippingFeeInSubunits } = shippingPreferences;
  const isOwn = shippingPreference === SHIPPING_PREFERENCE_OWN;
  const isPlatform = shippingPreference === SHIPPING_PREFERENCE_PLATFORM;

  if (
    isPlatform &&
    !hasValidPlatformShippingDetails({
      shippingPackage: shippingPreferences.defaultPackage,
      shippingOrigin: shippingPreferences.defaultOrigin,
    })
  ) {
    return null;
  }

  return {
    id: listing.id,
    publicData: {
      shippingPreference,
      ...(isOwn
        ? {
            shippingPriceInSubunitsOneItem: defaultShippingFeeInSubunits,
            shippingPriceInSubunitsAdditionalItems: defaultShippingFeeInSubunits,
          }
        : {
            shippingPriceInSubunitsOneItem: null,
            shippingPriceInSubunitsAdditionalItems: null,
          }),
    },
    privateData: {
      ...(isPlatform
        ? {
            shippingPackage: shippingPreferences.defaultPackage,
            shippingOrigin: shippingPreferences.defaultOrigin,
          }
        : {
            shippingPackage: null,
            shippingOrigin: null,
          }),
    },
  };
};

export const saveShippingPreferencesThunk = createAsyncThunk(
  'ShippingPreferencesPage/saveShippingPreferences',
  async (
    { values, previousPreference, applyToPublishedListings = false },
    { dispatch, extra: sdk, rejectWithValue }
  ) => {
    const shippingPreferences = buildShippingPreferences(values);
    const profileParams = {
      protectedData: {
        shippingPreferences,
      },
    };

    try {
      const updateProfileResponse = await sdk.currentUser.updateProfile(profileParams, {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      });
      const entities = denormalisedResponseEntities(updateProfileResponse);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      dispatch(setCurrentUser(entities[0]));

      if (
        !applyToPublishedListings ||
        previousPreference === shippingPreferences.shippingPreference
      ) {
        return { currentUser: entities[0], batchResult: null };
      }

      const listings = await queryAllOwnListings(sdk);
      const relevantListings = listings.filter(listing =>
        shouldUpdateListing(listing, previousPreference)
      );
      const batchResult = { updated: [], skipped: [], failed: [] };

      for (const listing of relevantListings) {
        const updateParams = buildListingUpdate(listing, shippingPreferences);
        if (!updateParams) {
          batchResult.skipped.push(listing.id.uuid);
          continue;
        }

        try {
          await sdk.ownListings.update(updateParams, { expand: true });
          batchResult.updated.push(listing.id.uuid);
        } catch (error) {
          // Keep the batch retryable by not aborting subsequent listings.
          // eslint-disable-next-line no-console
          console.error('Failed to update listing shipping preferences', {
            listingId: listing.id.uuid,
            error,
          });
          batchResult.failed.push(listing.id.uuid);
        }
      }

      return { currentUser: entities[0], batchResult };
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

export const saveShippingPreferences = params => dispatch => {
  return dispatch(saveShippingPreferencesThunk(params)).unwrap();
};

const shippingPreferencesSlice = createSlice({
  name: 'ShippingPreferencesPage',
  initialState: {
    saveInProgress: false,
    saveError: null,
    saveSuccess: false,
    batchResult: null,
  },
  reducers: {
    clearShippingPreferencesSave: state => {
      state.saveInProgress = false;
      state.saveError = null;
      state.saveSuccess = false;
      state.batchResult = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(saveShippingPreferencesThunk.pending, state => {
        state.saveInProgress = true;
        state.saveError = null;
        state.saveSuccess = false;
        state.batchResult = null;
      })
      .addCase(saveShippingPreferencesThunk.fulfilled, (state, action) => {
        state.saveInProgress = false;
        state.saveSuccess = true;
        state.batchResult = action.payload.batchResult;
      })
      .addCase(saveShippingPreferencesThunk.rejected, (state, action) => {
        state.saveInProgress = false;
        state.saveError = action.payload;
      });
  },
});

export const { clearShippingPreferencesSave } = shippingPreferencesSlice.actions;

export default shippingPreferencesSlice.reducer;

export const loadData = () => {
  return fetchCurrentUser();
};
